import type { RoomRecord } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';

/**
 * Pure room-selection helpers for `<ProfileSynchronizer>` (auth/synchronizer.tsx).
 *
 * Extracted into a dependency-free module so the route-aware room selection read-after-write fix is
 * unit-testable in isolation, mirroring the pure helpers in
 * routes/root/root-room-sync.ts. Per rule: think-in-systems (ai.knowledge/global).
 */

/**
 * Pick which room the synchronizer should treat as active.
 *
 * Priority (route-aware room selection): the room the user is actually on (URL route) once its
 * record is known wins over the cached `lastRoomId`, which wins over the
 * first-room bootstrap fallback. Before this, the route was never consulted, so
 * a navigated-to room missing from a stale/replica-lagged list silently fell
 * back to the first (old, "last-created") studio — the wrong-studio symptom.
 */
export const resolveActiveRoomId = (
  activeRoomRouteId: T_Maybe<RoomRecord['id']>,
  currentRoomId: T_Maybe<RoomRecord['id']>,
  roomsMap: Record<string, RoomRecord>
): T_Maybe<RoomRecord['id']> => {
  if (activeRoomRouteId && roomsMap[activeRoomRouteId]) {
    return activeRoomRouteId;
  }

  if (currentRoomId && roomsMap[currentRoomId]) {
    return currentRoomId;
  }

  return (Object.keys(roomsMap) as RoomRecord['id'][])[0];
};

/**
 * Whether to overwrite the persisted `lastRoomId`.
 *
 * route-aware room selection: never clobber it to a stale fallback while the user is on a specific
 * room route whose record has not surfaced yet (read-after-write lag) — that is
 * what flipped the studio identity to the old "last-created" studio. Otherwise,
 * overwrite only when the resolved room actually changed.
 */
export const shouldOverwriteLastRoomId = (
  resolvedRoomId: T_Maybe<RoomRecord['id']>,
  currentRoomId: T_Maybe<RoomRecord['id']>,
  activeRoomRouteId: T_Maybe<RoomRecord['id']>,
  roomsMap: Record<string, RoomRecord>
): boolean => {
  if (resolvedRoomId === currentRoomId) {
    return false;
  }

  if (activeRoomRouteId && !roomsMap[activeRoomRouteId]) {
    return false;
  }

  return true;
};
